import React, { useMemo, memo } from 'react';

const keyframeCSS = `
  @keyframes splitPrizePulse {
    0%, 100% { box-shadow: inset 0 0 8px rgba(255,255,255,0.04), 0 0 4px rgba(0,0,0,0.2); }
    50% { box-shadow: inset 0 0 14px rgba(255,255,255,0.1), 0 0 8px rgba(0,0,0,0.1); }
  }
  @keyframes splitDotGlow {
    0%, 100% { box-shadow: 0 0 4px currentColor, 0 0 8px currentColor; }
    50% { box-shadow: 0 0 8px currentColor, 0 0 16px currentColor, 0 0 24px currentColor; }
  }
  @keyframes splitBoardGlow {
    0%, 100% { box-shadow: inset 0 0 20px rgba(0,0,0,0.4); }
    50% { box-shadow: inset 0 0 25px rgba(0,0,0,0.3), 0 0 8px var(--board-accent, rgba(52,152,219,0.1)); }
  }
  @keyframes splitLedBlink {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
  @keyframes splitTitleNeon {
    0%, 19%, 21%, 23%, 25%, 54%, 56%, 100% { opacity: 1; }
    20%, 24%, 55% { opacity: 0.7; }
  }
  @keyframes eliminatePrize {
    0% { opacity: 1; transform: scale(1); }
    30% { opacity: 0.1; transform: scale(0.97); }
    100% { opacity: 0.25; transform: scale(1); }
  }

  /* Mobile: ultra compact */
  @media (max-width: 900px) {
    .split-board-panel {
      padding: 4px 3px !important;
      border-radius: 6px !important;
      min-width: 0 !important;
      width: 100% !important;
      max-width: 150px !important;
      gap: 1px !important;
    }
    .split-board-title {
      font-size: 0.5rem !important;
      padding: 2px 0 !important;
      margin-bottom: 2px !important;
    }
    .split-board-row {
      padding: 1px 4px !important;
      font-size: 0.5rem !important;
      gap: 2px !important;
      border-radius: 2px !important;
    }
    .split-board-dot {
      width: 3px !important;
      height: 3px !important;
    }
  }
  @media (max-width: 480px) {
    .split-board-panel {
      max-width: 130px !important;
    }
    .split-board-row {
      padding: 1px 3px !important;
      font-size: 0.45rem !important;
    }
    .split-board-title {
      font-size: 0.42rem !important;
    }
  }
`;

function formatPrize(prize) {
  if (prize && typeof prize === 'object' && 'label' in prize) {
    if (prize.value === 0 && prize.label) return prize.label;
    return formatEuro(prize.value);
  }
  return formatEuro(prize);
}

function formatEuro(value) {
  if (typeof value !== 'number') return String(value);
  if (value >= 1000) return '\u20AC' + value.toLocaleString('it-IT');
  return value.toLocaleString('it-IT', {
    style: 'currency', currency: 'EUR',
    minimumFractionDigits: value < 1 ? 2 : 0, maximumFractionDigits: 2,
  });
}

function usePrizeList(allPrizes, boxes) {
  return useMemo(() => {
    const normalized = allPrizes.map((p) =>
      typeof p === 'object' && p !== null ? { value: p.value ?? 0, label: p.label ?? null } : { value: p, label: null }
    );
    const sorted = [...normalized].sort((a, b) => a.value - b.value);
    const getKey = (p) => (p.value === 0 && p.label ? `text:${p.label}` : `val:${p.value}`);
    const eliminatedCounts = new Map();
    boxes.forEach((box) => {
      if (box.is_opened && (box.prize_value !== undefined || box.prize_label)) {
        const key = (box.prize_value === 0 || box.prize_value === undefined) && box.prize_label
          ? `text:${box.prize_label}` : `val:${box.prize_value}`;
        eliminatedCounts.set(key, (eliminatedCounts.get(key) || 0) + 1);
      }
    });
    const usedCounts = new Map();
    return sorted.map((prize) => {
      const key = getKey(prize);
      const elimCount = eliminatedCounts.get(key) || 0;
      const usedSoFar = usedCounts.get(key) || 0;
      const isEliminated = usedSoFar < elimCount;
      usedCounts.set(key, usedSoFar + 1);
      return { ...prize, eliminated: isEliminated };
    });
  }, [allPrizes, boxes]);
}

/* ── Single column board (Blue or Red) ── */
export function PrizeBoardColumn({ boxes = [], allPrizes = [], side = 'low' }) {
  const prizeList = usePrizeList(allPrizes, boxes);
  const mid = Math.ceil(prizeList.length / 2);
  const prizes = side === 'low' ? prizeList.slice(0, mid) : prizeList.slice(mid);

  const isBlue = side === 'low';
  const accentColor = isBlue ? '#3498db' : '#e74c3c';
  const title = isBlue ? 'BLU' : 'ROSSO';

  return (
    <div className="split-board-panel" style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '3px',
      padding: '12px 8px',
      minWidth: '0',
      width: '100%',
      maxWidth: '170px',
      background: 'linear-gradient(180deg, rgba(5,5,18,0.95), rgba(8,8,25,0.98))',
      borderRadius: '12px',
      border: `1px solid ${accentColor}25`,
      position: 'relative',
      overflow: 'hidden',
      '--board-accent': `${accentColor}20`,
      animation: 'splitBoardGlow 4s ease-in-out infinite',
    }}>
      <style>{keyframeCSS}</style>

      {/* Top LED strip */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: `linear-gradient(90deg, transparent, ${accentColor}80, transparent)`,
        animation: 'splitLedBlink 3s ease-in-out infinite',
      }} />

      {/* Title */}
      <div className="split-board-title" style={{
        fontFamily: 'var(--font-display)',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        color: accentColor,
        textAlign: 'center',
        marginBottom: '6px',
        padding: '6px 0',
        borderBottom: `1px solid ${accentColor}20`,
        textShadow: `0 0 12px ${accentColor}60`,
        animation: 'splitTitleNeon 3s ease-in-out infinite',
      }}>
        {title}
      </div>

      {/* Prize rows */}
      {prizes.map((prize, i) => (
        <PrizeRow key={i} prize={prize} isLow={isBlue} index={i} accentColor={accentColor} />
      ))}
    </div>
  );
}

const PrizeRow = memo(function PrizeRow({ prize, isLow, index, accentColor }) {
  const eliminated = prize.eliminated;
  const isPaccoNero = prize.label === 'SCATOLA MISTERIOSA' || prize.is_pacco_nero;

  const activeBg = isPaccoNero
    ? 'linear-gradient(135deg, rgba(15,15,15,0.9), rgba(30,30,30,0.8))'
    : isLow
      ? 'linear-gradient(135deg, rgba(20,30,100,0.7), rgba(30,45,120,0.5))'
      : 'linear-gradient(135deg, rgba(160,20,20,0.7), rgba(180,30,30,0.5))';

  const dotColor = isPaccoNero ? '#888' : accentColor;
  const textColor = isPaccoNero && !eliminated ? '#ccc' : eliminated ? 'rgba(80,80,100,0.3)' : '#f0f0f5';
  const borderColor = isPaccoNero ? 'rgba(100,100,100,0.3)' : `${accentColor}30`;

  return (
    <div className="split-board-row" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 8px',
      borderRadius: '4px',
      background: eliminated ? 'rgba(30,30,40,0.3)' : activeBg,
      border: eliminated ? '1px solid rgba(50,50,60,0.15)' : `1px solid ${borderColor}`,
      fontFamily: isPaccoNero ? 'var(--font-display)' : 'var(--font-mono)',
      fontSize: isPaccoNero ? '0.65rem' : '0.78rem',
      fontWeight: isPaccoNero ? 700 : 600,
      color: textColor,
      textDecoration: eliminated ? 'line-through' : 'none',
      opacity: eliminated ? 0.3 : 1,
      animation: eliminated ? 'none' : 'splitPrizePulse 3s ease-in-out infinite',
      animationDelay: `${index * 0.15}s`,
      transition: 'all 0.4s ease',
      position: 'relative',
      overflow: 'hidden',
      letterSpacing: isPaccoNero ? '0.05em' : 'normal',
    }}>
      <div className="split-board-dot" style={{
        width: '5px',
        height: '5px',
        borderRadius: '50%',
        backgroundColor: eliminated ? 'rgba(60,60,70,0.3)' : dotColor,
        flexShrink: 0,
        animation: eliminated ? 'none' : 'splitDotGlow 2s ease-in-out infinite',
        animationDelay: `${index * 0.1}s`,
        color: dotColor,
        transition: 'all 0.4s ease',
      }} />
      <span style={{
        flex: 1,
        textAlign: 'center',
        whiteSpace: 'nowrap',
        textShadow: isPaccoNero && !eliminated ? '0 0 8px rgba(100,100,100,0.3)' : eliminated ? 'none' : `0 0 6px ${accentColor}25`,
      }}>
        {isPaccoNero ? 'SCATOLA MISTERIOSA' : formatPrize(prize)}
      </span>
    </div>
  );
});

export default PrizeBoardColumn;
